// A birthday is a calendar date, not a moment in time: it is the same day
// wherever the person reading it happens to be. So this formatter works in UTC
// and the parser below builds its dates there too. The timestamp formatter
// beneath it stays local, because a memory really was left at an instant.
const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** A plain calendar date, year-month-day. What an <input type="date"> produces. */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The same date written the way it is said aloud here: 20-01-1958, 20/01/1958. */
const UK_ORDER_DATE = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/;

function fromParts(year: number, month: number, day: number): Date | null {
  const date = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC rolls a day that does not exist forward -- 31 February becomes
  // 3 March -- so a date that survives the round trip is a real one.
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
}

function parseCalendarDate(value: string): Date | null {
  const match = CALENDAR_DATE.exec(value.trim());
  if (match === null) return null;
  return fromParts(Number(match[1]), Number(match[2]), Number(match[3]));
}

function parse(isoDate: string): Date | null {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whether a value is a real calendar date, year-month-day. An empty string is not. */
export function isCalendarDate(value: string): boolean {
  return parseCalendarDate(value) !== null;
}

/**
 * A date rewritten as year-month-day so that a date picker can show it, or an
 * empty string when it cannot be read with any confidence.
 *
 * The day-first form is understood because that is how a date is written here,
 * and because the free-text field this replaces invited exactly that. Anything
 * more ambiguous is refused rather than guessed at: a picker left blank asks to
 * be filled in, whereas one showing the wrong day looks settled.
 */
export function toCalendarDate(value: string): string {
  const trimmed = value.trim();
  if (isCalendarDate(trimmed)) return trimmed;

  const match = UK_ORDER_DATE.exec(trimmed);
  if (match === null) return '';

  const day = Number(match[1]);
  const month = Number(match[2]);
  if (fromParts(Number(match[3]), month, day) === null) return '';

  return `${match[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Formats a calendar date for display, e.g. "2 July 2026". Empty when unreadable. */
export function formatDate(isoDate: string): string {
  const date = parseCalendarDate(isoDate);
  return date ? dateFormatter.format(date) : '';
}

/** Formats an ISO 8601 timestamp for display, e.g. "2 July 2026, 18:20". */
export function formatDateTime(isoDate: string): string {
  const date = parse(isoDate);
  return date ? dateTimeFormatter.format(date) : '';
}

/** Formats a byte count for the upload page, e.g. "3.4 MB". */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
